import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SelfServe360View, SelfServeUiContext } from '../contexts/self-serve-ui.context';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { get } from 'lodash';
import { EndUserResult, ExtensionMobilityRef, VoicemailRef } from '../../shared/models/generated/ziroModels';
import { ZiroIcons } from '../../shared/models/ziro-icons.enum';
import { ZiroFormAbstractDirective } from '../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../forms/ziro-form-state.service';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../forms/fields/text/ziro-text.component';
import { SelfServeOptionsContext } from '../contexts/self-serve-options.context';
import { ButtonStyles } from '../../button/button.component';

interface FormData {
  pin: string;
  pinConfirm: string;
  selfServeVoicemailPinResetComplexity: string;
}

enum ValidationMsg {
  NotNumeric = 'tkey;validators.global.numeric.error',
  DoNotMatch = 'tkey;selfserve.pin.do.not.match.text',
}

const validators = {
  pinIsNumeric: (val: string) =>
    !!val && /(^[0-9]+$)/.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
  doPinsMatch: (val: string, val2: string) =>
    !val || (!!val && !!val2 && val === val2) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
};

@Component({
  templateUrl: './self-serve-pin-reset.abstract.component.html',
  standalone: false,
})
export class SelfServePinResetAbstractComponent
  extends ZiroFormAbstractDirective<FormData>
  implements OnInit, OnDestroy
{
  private selfServeUiContext = inject(SelfServeUiContext);
  private selfServeOptionsContext = inject(SelfServeOptionsContext);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  protected _ziroFormStateService: ZiroFormStateService;

  isLoading = true;
  isPending = false;
  headerTranslationKey: string;
  headingDataAutomation: string;
  ziroIcons = ZiroIcons;
  user: EndUserResult;
  selfServe360View = {} as SelfServe360View;
  voicemailRef: VoicemailRef;
  extensionMobilityRef: ExtensionMobilityRef;
  private _vmId = get(this.route, 'snapshot.params.voicemailId');
  private _emId = get(this.route, 'snapshot.params.extensionMobilityId');

  private _pinComplexityMessage: string | null = null;

  formConfig: ZiroFormConfig = {
    fields: {
      pin: {
        label: 'tkey;selfserve.pin.placeholder',
        dataAutomation: 'self-serve-pin-reset-input',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
        required: true,
        validation: [
          {
            validator: (val: string) => validators.pinIsNumeric(val),
            message: ValidationMsg.NotNumeric,
            injectValuesFromFields: ['pinConfirm'],
          },
        ],
      },
      pinConfirm: {
        label: 'tkey;selfserve.pin.confirm.placeholder',
        dataAutomation: 'self-serve-pin-reset-confirm-input',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
        required: true,
        validation: [
          {
            validator: (val: string, val2: string) => validators.doPinsMatch(val, val2),
            message: ValidationMsg.DoNotMatch,
            injectValuesFromFields: ['pin'],
          },
        ],
      },
      selfServeVoicemailPinResetComplexity: {
        label: 'tkey;selfserve.pin.complexity.label',
        dataAutomation: 'self-serve-pin-complexity-input',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT, readonly: true }),
        disabled: () => true,
        valExcluded: () => true,
        hidden: () => !this._pinComplexityMessage || !this._vmId,
      },
    },
  };

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this._initSelfServeOptions();
    this._initSelfServe();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  resetPin = (): Observable<any> => {
    return of(Promise.resolve());
  };

  onPinResetSuccess = (): void => {};

  resetPinClick(): Observable<void> {
    return new Observable((subscriber) => {
      this.isPending = true;
      this.resetPin().subscribe({
        next: () => {
          this.onPinResetSuccess();
          subscriber.next(null);
          subscriber.complete();
        },
        error: (error) => {
          this.isPending = false;
          this.isSubmitting = false;
          this.isFormSubmitted = false;
          this._ziroFormStateService.setIsFormDirty(false);
          subscriber.error(error);
          subscriber.complete();
        },
      });
    });
  }

  onPinResetSuccessBase: () => void = () => {
    this.isPending = false;
    this.router.navigateByUrl(`self-serve/user/${encodeURIComponent(this.user.ref.username)}`);
  };

  private _initSelfServeOptions: () => void = () => {
    const subscription = this.selfServeOptionsContext.state$.subscribe((options) => {
      this._pinComplexityMessage = options?.selfServeVoicemailPinResetComplexity;

      const nextEntity = {
        ...this.entity,
        selfServeVoicemailPinResetComplexity: options?.selfServeVoicemailPinResetComplexity,
      };
      this.entitySource.next(nextEntity);
    });

    this._subscriptions.add(subscription);
  };

  private _initSelfServe: () => void = () => {
    if (!this.selfServeUiContext.selfServe360View) {
      const username = get(this.route, 'snapshot.params.username');
      this.selfServeUiContext.initUserDetails(username);
    }

    const subscription = this.selfServeUiContext.selfServe360View$.subscribe((selfServe360View: SelfServe360View) => {
      if (selfServe360View) {
        this.selfServe360View = selfServe360View;

        if (this._vmId) {
          this.voicemailRef = this.selfServe360View.voicemails.find((vm: VoicemailRef) => vm.id === this._vmId);
          this.user = selfServe360View.endUsers[0];
        } else if (this._emId) {
          this.extensionMobilityRef = this.selfServe360View.extensionMobilities.find(
            (em: ExtensionMobilityRef) => em.id === this._emId,
          );
          this.user = selfServe360View.endUsers.find(
            (endUser: EndUserResult) => endUser.ref.serverId === this.extensionMobilityRef.serverId,
          );
        }

        if (!this.voicemailRef && !this.extensionMobilityRef) {
          const username = get(this.route, 'snapshot.params.username');
          this.router.navigateByUrl(`self-serve/user/${encodeURIComponent(username)}`);
          return;
        }

        this.isLoading = false;
      }
    });
    this._subscriptions.add(subscription);
  };

  protected submit(): Observable<void> {
    return this.resetPinClick();
  }

  protected readonly ButtonStyles = ButtonStyles;
}
